import { useEffect, useRef, useState } from "react";
import { cacheGet, cachedFetch } from "@/lib/cache";
import CommentBox from "@/components/CommentBox";
import CommentItem from "@/components/CommentItem";
import { PeriodoTag } from "@/components/PeriodoTag";
import { SectionCard, Field } from "@/components/modelo/SectionCard";
import ModeloHeader from "@/components/modelo/ModeloHeader";
import PasosList from "@/components/modelo/PasosList";
import ModeloDialogs from "@/components/modelo/ModeloDialogs";
import ElegirHormaDialog from "@/components/modelo/ElegirHormaDialog";
import CambiarRutaDialog from "@/components/modelo/CambiarRutaDialog";
import { FASE_DOT } from "@/components/modelo/constants";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  money, fechaHora, fechaSolo, dias, labelRol, colorRol,
} from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import LazyImage from "@/components/LazyImage";
import NavFlechas from "@/components/NavFlechas";
import { ambiguosDeModelo, etiquetaPaso } from "@/lib/pasoLabel";
import { compararModelos } from "@/lib/orden";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import FechaInput from "@/components/FechaInput";
import {
  ChevronLeft, Check, X, Paperclip, Upload, FileText, Clock,
  Image as ImageIcon, History, Layers, MessageSquare,
} from "lucide-react";

export default function ModeloDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirmar = useConfirm();
  const role = user?.role;
  const [m, setM] = useState(() => cacheGet(`/modelos/${id}`) ?? null);
  const [listas, setListas] = useState(() => cacheGet("/listas") ?? {});
  const [archivos, setArchivos] = useState(() => cacheGet(`/modelos/${id}/archivos`) ?? []);
  const [historial, setHistorial] = useState(() => cacheGet(`/modelos/${id}/historial`) ?? []);
  const [plantillas, setPlantillas] = useState(() => cacheGet("/plantillas") ?? []);
  const [colecciones, setColecciones] = useState(() => cacheGet("/colecciones") ?? []);
  const [mencionables, setMencionables] = useState(() => cacheGet(`/modelos/${id}/usuarios-mencionables`) ?? []);
  const [disenadores, setDisenadores] = useState(() => cacheGet("/disenadores") ?? []);
  const [lightbox, setLightbox] = useState(null);
  const [identEdit, setIdentEdit] = useState(false);
  const [identDraft, setIdentDraft] = useState({ nombre: "", sku: "" });
  const [dupOpen, setDupOpen] = useState(false);
  const [dupNombre, setDupNombre] = useState("");
  const [dupColeccion, setDupColeccion] = useState("");
  const [dupSel, setDupSel] = useState({ ficha: true, organizacion: true, comercial: true, multimedia: true, notas: true, proceso: false });
  const [reasignOpen, setReasignOpen] = useState(false);
  const [reasignColeccion, setReasignColeccion] = useState("");
  const [reajusteOpen, setReajusteOpen] = useState(false);
  const [reajustePaso, setReajustePaso] = useState("");
  const [reajusteMotivo, setReajusteMotivo] = useState("");
  const [depuradoOpen, setDepuradoOpen] = useState(false);
  const [depuradoMotivo, setDepuradoMotivo] = useState("");
  const [reactivarOpen, setReactivarOpen] = useState(false);
  const [reactivarMotivo, setReactivarMotivo] = useState("");
  const [accionBusy, setAccionBusy] = useState(false);
  const [hormaOpen, setHormaOpen] = useState(false);
  const [rutaOpen, setRutaOpen] = useState(false);
  const fileRefs = useRef({});
  const docFileIdsRef = useRef({});
  const [replaceDlg, setReplaceDlg] = useState({ open: false, tipo: null, paso_numero: null, file: null, motivo: "" });

  const loadArchivos = () => cachedFetch(`/modelos/${id}/archivos`, setArchivos);
  const loadHistorial = () => cachedFetch(`/modelos/${id}/historial`, setHistorial);
  const loadModelo = () => cachedFetch(`/modelos/${id}`, setM);

  useEffect(() => {
    loadModelo().catch(() => navigate("/"));
    cachedFetch("/listas", setListas);
    cachedFetch("/plantillas", setPlantillas);
    cachedFetch("/colecciones", setColecciones).catch(() => {});
    cachedFetch(`/modelos/${id}/usuarios-mencionables`, setMencionables).catch(() => {});
    cachedFetch("/disenadores", setDisenadores).catch(() => {});
    loadArchivos();
    loadHistorial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const refreshAll = () => { loadModelo(); loadArchivos(); loadHistorial(); };

  // Hermanos de la misma colección, en el MISMO orden que la tabla de ColeccionDetalle
  // (pendientes A-Z, luego completados/liberados A-Z, luego depurados A-Z). Para las flechas.
  const [hermanos, setHermanos] = useState([]);
  useEffect(() => {
    const cid = m?.coleccion_id;
    if (!cid) return;
    api.get(`/modelos?coleccion_id=${cid}`).then((r) => {
      const ordenados = [...r.data].sort(compararModelos);
      setHermanos(ordenados.map((x) => x.id));
    }).catch(() => {});
  }, [m?.coleccion_id]);
  const idxHermano = hermanos.indexOf(id);
  const irHermano = (dir) => {
    const t = hermanos[idxHermano + dir];
    if (t) navigate(`/modelos/${t}`);
  };

  // Espejo síncrono del documento vigente por paso (evita la condición de carrera
  // al reemplazar "al instante" antes de que el estado `m` termine de refrescarse).
  useEffect(() => {
    if (m?.pasos) {
      const map = {};
      m.pasos.forEach((p) => { map[p.numero] = p.documento_file_id || null; });
      docFileIdsRef.current = map;
    }
  }, [m]);

  const col = colecciones.find((c) => c.id === m?.coleccion_id);
  const bloqueada = col?.estado === "completada";
  const puedeSeccion = (sec) => !bloqueada && (user?.es_admin || (user?.perms?.secciones || []).includes(sec));
  const puedeFase = (fase) => !bloqueada && (user?.es_admin || (user?.perms?.fases || []).includes(fase));
  const puedeMultimedia = (tipo) => !bloqueada && (user?.es_admin || (user?.perms?.multimedia || []).includes(tipo));
  const puedeAccion = (a) => user?.es_admin || !!user?.perms?.acciones?.[a];
  const puedeDescargar = user?.es_admin || !!user?.perms?.acciones?.descargar_archivos;
  // El diseñador se asigna con su propia acción; el resto de la organización, con la sección.
  const puedeOrg = puedeSeccion("organizacion");
  const puedeDisenador = !bloqueada && puedeAccion("asignar_disenador");
  // El paso 1 (horma) lo elige quien trabaja la fase Horma o Diseño (el diseñador elige del catálogo).
  const puedeElegirHorma = !m?.depurado && (puedeFase("Desarrollo · Horma") || puedeFase("Diseño"));

  const reabrirColeccion = async () => {
    try {
      await api.post(`/colecciones/${col.id}/reabrir`);
      toast.success("Colección reabierta");
      api.get("/colecciones").then((r) => setColecciones(r.data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const asignarColeccion = async (coleccion_id) => {
    try {
      await api.patch(`/modelos/${id}/coleccion`, { coleccion_id });
      toast.success("Modelo reasignado");
      refreshAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const eliminarModelo = async () => {
    if (!(await confirmar({ titulo: `¿Eliminar el modelo "${m.nombre}"?`, mensaje: "El modelo se archiva: sale de la colección, Kanban y Dashboard, pero su ficha, documentos e historial se conservan.\n\nEsto no es depurar. Para descontinuar un modelo sin terminarlo, usa Acciones → Depurar dentro de su ficha.", accion: "Eliminar modelo", peligro: true }))) return;
    try {
      await api.delete(`/modelos/${id}`);
      toast.success("Modelo eliminado");
      navigate(`/colecciones/${m.coleccion_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const abrirDuplicar = () => {
    setDupNombre(`${m.nombre} (copia)`);
    setDupColeccion(m.coleccion_id || "");
    setDupSel({ ficha: true, organizacion: true, comercial: true, multimedia: true, notas: true, proceso: false });
    setDupOpen(true);
  };
  const confirmarDuplicar = async () => {
    if (!dupColeccion) { toast.error("Elige una colección"); return; }
    setAccionBusy(true);
    try {
      const { data } = await api.post(`/modelos/${id}/duplicar`, {
        coleccion_id: dupColeccion,
        nombre: dupNombre,
        incluir_ficha: dupSel.ficha,
        incluir_organizacion: dupSel.organizacion,
        incluir_comercial: dupSel.comercial,
        incluir_multimedia: dupSel.multimedia,
        incluir_notas: dupSel.notas,
        incluir_proceso: dupSel.proceso,
      });
      toast.success("Modelo duplicado");
      setDupOpen(false);
      navigate(`/modelos/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setAccionBusy(false); }
  };
  const abrirReasignar = () => {
    setReasignColeccion(m.coleccion_id || "");
    setReasignOpen(true);
  };
  const confirmarReasignar = async () => {
    if (!reasignColeccion) { toast.error("Elige una colección"); return; }
    setAccionBusy(true);
    try {
      await api.post(`/modelos/${id}/reasignar`, { coleccion_id: reasignColeccion });
      toast.success("Colección reasignada");
      setReasignOpen(false);
      refreshAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setAccionBusy(false); }
  };

  const abrirReajuste = () => {
    setReajustePaso("");
    setReajusteMotivo("");
    setReajusteOpen(true);
  };
  const abrirDepurado = () => {
    setDepuradoMotivo("");
    setDepuradoOpen(true);
  };
  const confirmarDepurado = async () => {
    if (depuradoMotivo.trim().length < 10) return;
    setAccionBusy(true);
    try {
      await api.post(`/modelos/${id}/depurado`, { motivo: depuradoMotivo.trim() });
      toast.success("Modelo depurado");
      setDepuradoOpen(false);
      refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setAccionBusy(false); }
  };
  const abrirReactivar = () => {
    setReactivarMotivo("");
    setReactivarOpen(true);
  };
  const confirmarReactivar = async () => {
    if (reactivarMotivo.trim().length < 10) return;
    setAccionBusy(true);
    try {
      await api.post(`/modelos/${id}/reactivar`, { motivo: reactivarMotivo.trim() });
      toast.success("Modelo reactivado");
      setReactivarOpen(false);
      refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setAccionBusy(false); }
  };
  const confirmarReajuste = async () => {
    if (!reajustePaso || reajusteMotivo.trim().length < 10) return;
    setAccionBusy(true);
    try {
      await api.post(`/modelos/${id}/reajuste`, { paso_numero: Number(reajustePaso), motivo: reajusteMotivo.trim() });
      toast.success("Reajuste aplicado");
      setReajusteOpen(false);
      refreshAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setAccionBusy(false); }
  };

  const comentar = async (texto, menciones) => {
    try {
      await api.post(`/modelos/${id}/comentarios`, { texto, menciones });
      loadModelo();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const guardarIdentidad = async () => {
    try {
      await saveSeccion("identidad", identDraft);
      setIdentEdit(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveSeccion = async (seccion, datos) => {
    await api.patch(`/modelos/${id}`, { seccion, datos });
    toast.success("Cambios guardados");
    refreshAll();
  };

  const toggleStep = async (paso, nuevoValor) => {
    try {
      await api.post(`/modelos/${id}/pasos/${paso.numero}`, { hecho: nuevoValor });
      refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // ¿El paso ya tiene un documento vigente? Mismo criterio que el backend (existe archivo is_current),
  // sin importar si el paso está marcado `hecho` o quedó desmarcado por un Reajuste.
  const pasoTieneDocActual = (n) =>
    !!docFileIdsRef.current[n] ||
    archivos.some((a) => a.tipo === "documento_paso" && a.paso_numero === n && a.is_current);

  const upload = async (tipo, paso_numero, file, motivo) => {
    if (!file) return;
    // Reemplazo de documento vigente: exigir motivo (mín. 10 chars) vía diálogo.
    // Mismo criterio que el backend (documento vigente existente) para que el modal aparezca
    // también en pasos desmarcados por Reajuste (hecho:false + documento vigente).
    if (tipo === "documento_paso" && motivo == null && pasoTieneDocActual(paso_numero)) {
      setReplaceDlg({ open: true, tipo, paso_numero, file, motivo: "" });
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tipo", tipo);
    if (paso_numero != null) fd.append("paso_numero", paso_numero);
    if (motivo) fd.append("motivo", motivo);
    try {
      const { data } = await api.post(`/modelos/${id}/archivos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Registrar de inmediato el documento del paso para que un reemplazo posterior
      // abra el diálogo de motivo aunque el refetch aún no haya terminado.
      if (tipo === "documento_paso" && paso_numero != null && data?.id) {
        docFileIdsRef.current[paso_numero] = data.id;
      }
      toast.success(motivo ? "Documento reemplazado" : "Archivo subido");
      refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const confirmReplace = async () => {
    const mo = (replaceDlg.motivo || "").trim();
    if (mo.length < 10) return;
    const { tipo, paso_numero, file } = replaceDlg;
    setReplaceDlg({ open: false, tipo: null, paso_numero: null, file: null, motivo: "" });
    await upload(tipo, paso_numero, file, mo);
  };

  const triggerUpload = (key, tipo, paso_numero) => {
    const input = fileRefs.current[key];
    if (input) {
      input.onchange = (e) => upload(tipo, paso_numero, e.target.files[0]);
      input.click();
    }
  };

  if (!m) return (
    <div className="p-6 lg:p-8 max-w-7xl" data-testid="modelo-shell">
      <div className="h-5 w-40 bg-slate-100 rounded mb-4 animate-pulse" />
      <div className="h-24 rounded-xl bg-slate-100 animate-pulse mb-6" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
        <div className="lg:col-span-4 space-y-6">
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      </div>
    </div>
  );

  const est = m.estatus;
  const ambiguos = ambiguosDeModelo(m.pasos);
  const pasosPorFase = (fase) => m.pasos.filter((p) => p.fase === fase).sort((a, b) => a.numero - b.numero);
  const plantillaDe = (numero) => plantillas.find((p) => p.paso_numero === numero)?.plantilla;
  const formatoDe = (numero) => plantillas.find((p) => p.paso_numero === numero);
  const versionesDe = (numero) => archivos.filter((a) => a.tipo === "documento_paso" && a.paso_numero === numero).sort((a, b) => b.version - a.version);
  const boceto = m.multimedia.boceto_file_id ? fileUrl(m.multimedia.boceto_file_id) : m.multimedia.boceto_url;
  const fotoFinal = m.multimedia.foto_final_file_id ? fileUrl(m.multimedia.foto_final_file_id) : m.multimedia.foto_final_url;

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <input type="file" className="hidden" ref={(el) => (fileRefs.current.__shared = el)} />
      <button onClick={() => navigate(`/colecciones/${m.coleccion_id}`)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4 transition-colors">
        <ChevronLeft size={16} /> Volver a la colección
      </button>

      <ModeloHeader
        m={m} identEdit={identEdit} identDraft={identDraft} setIdentDraft={setIdentDraft}
        guardarIdentidad={guardarIdentidad} setIdentEdit={setIdentEdit}
        puedeSeccion={puedeSeccion} bloqueada={bloqueada} puedeAccion={puedeAccion}
        reabrirColeccion={reabrirColeccion} eliminarModelo={eliminarModelo}
        abrirDuplicar={abrirDuplicar} abrirReasignar={abrirReasignar} abrirReajuste={abrirReajuste} abrirDepurado={abrirDepurado} abrirReactivar={abrirReactivar}
        irHermano={irHermano}
        hermanoPrevDisabled={idxHermano <= 0}
        hermanoNextDisabled={idxHermano < 0 || idxHermano >= hermanos.length - 1}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
        {/* ---------------- Columna principal ---------------- */}
        <div className="contents lg:block lg:col-span-8 lg:space-y-6">
          {/* Multimedia */}
          <div className="order-3 md:col-span-1"><SectionCard title="Multimedia" icon={ImageIcon} testid="section-multimedia">
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Boceto / Foto", src: boceto, tipo: "boceto" },
                { label: "Foto final", src: fotoFinal, tipo: "foto_final" },
              ].map((img) => (
                <div key={img.tipo}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-slate-400 uppercase tracking-wide">{img.label}</span>
                    {puedeMultimedia(img.tipo) && (
                      <button onClick={() => triggerUpload(`mm-${img.tipo}`, img.tipo, null)} className="text-xs text-blue-600 flex items-center gap-1 hover:text-blue-700" data-testid={`upload-${img.tipo}`}>
                        <Upload size={12} /> Subir
                      </button>
                    )}
                    <input type="file" accept="image/*" className="hidden" ref={(el) => (fileRefs.current[`mm-${img.tipo}`] = el)} />
                  </div>
                  <div className="aspect-square rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden">
                    {img.src ? (
                      <button type="button" onClick={() => setLightbox({ src: img.src, label: img.label })} className="w-full h-full cursor-zoom-in" data-testid={`ver-imagen-${img.tipo}`}>
                        <LazyImage src={img.src} alt={img.label} containerClassName="w-full h-full bg-slate-50" imgClassName="object-contain" />
                      </button>
                    ) : <ImageIcon className="text-slate-300" size={32} />}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard></div>

          {/* Ficha técnica */}
          <div className="order-2 md:col-span-1"><SectionCard
            title="Ficha técnica" icon={Layers} section="ficha_tecnica" canEdit={puedeSeccion("ficha_tecnica")} testid="section-ficha"
            onSave={(d) => saveSeccion("ficha_tecnica", d)}
            renderEdit={{
              initial: () => ({ ...m.ficha_tecnica }),
              form: (d, set) => (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Material (upper)</Label><Input value={d.material} onChange={(e) => set({ ...d, material: e.target.value })} data-testid="ficha-material" /></div>
                  <div className="space-y-1.5"><Label>Suela</Label><Input value={d.suela} onChange={(e) => set({ ...d, suela: e.target.value })} data-testid="ficha-suela" /></div>
                </div>
              ),
            }}
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Material (upper)" value={m.ficha_tecnica.material} />
              <Field label="Suela" value={m.ficha_tecnica.suela} />
            </div>
          </SectionCard></div>

          {/* Proceso de desarrollo */}
          <div className="order-7 md:col-span-2"><PasosList
            pasosPorFase={pasosPorFase} puedeFase={puedeFase} toggleStep={toggleStep} triggerUpload={triggerUpload} puedeDescargar={puedeDescargar}
            formatoDe={formatoDe} plantillaDe={plantillaDe} versionesDe={versionesDe} fileRefs={fileRefs}
            fechaMeta={m.fecha_meta_prototipo} depurado={!!m.depurado}
            horma={m.horma} hormaAnterior={!!m.horma_anterior} bloqueoHorma={m.bloqueo_horma} puedeElegirHorma={puedeElegirHorma} abrirHorma={() => setHormaOpen(true)}
            ruta={m.ruta_info} abrirRuta={user?.es_admin && !m.depurado ? () => setRutaOpen(true) : null}
          /></div>
          <ElegirHormaDialog open={hormaOpen} onOpenChange={setHormaOpen} modelo={m} onElegida={refreshAll} />
          <CambiarRutaDialog open={rutaOpen} onOpenChange={setRutaOpen} modelo={m} onCambiada={refreshAll} />

          {/* Comercial */}
          <div className="order-4 md:col-span-2"><SectionCard
            title="Comercial" section="comercial" canEdit={puedeSeccion("comercial")} testid="section-comercial"
            onSave={(d) => saveSeccion("comercial", { costo_meta: d.costo_meta === "" ? null : Number(d.costo_meta), drop: d.drop, compra_inicial: d.compra_inicial === "" ? null : Number(d.compra_inicial) })}
            renderEdit={{
              initial: () => ({ costo_meta: m.comercial.costo_meta ?? "", drop: m.comercial.drop ?? "", compra_inicial: m.comercial.compra_inicial ?? "" }),
              form: (d, set) => (
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5"><Label>Costo meta (MXN)</Label><Input type="number" value={d.costo_meta} onChange={(e) => set({ ...d, costo_meta: e.target.value })} data-testid="com-costo" /></div>
                  <div className="space-y-1.5"><Label>Drop</Label><Input value={d.drop} onChange={(e) => set({ ...d, drop: e.target.value })} data-testid="com-drop" /></div>
                  <div className="space-y-1.5"><Label>Compra inicial</Label><Input type="number" value={d.compra_inicial} onChange={(e) => set({ ...d, compra_inicial: e.target.value })} data-testid="com-compra" /></div>
                </div>
              ),
            }}
          >
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Costo meta" value={money(m.comercial.costo_meta)} />
              <Field label="Drop" value={m.comercial.drop} />
              <Field label="Compra inicial" value={m.comercial.compra_inicial != null ? m.comercial.compra_inicial : "—"} />
            </div>
          </SectionCard></div>

          {/* Notas de desarrollo */}
          <div className="order-8 md:col-span-2"><SectionCard
            title="Notas de desarrollo" icon={FileText} section="notas" canEdit={puedeSeccion("notas")} testid="section-notas"
            onSave={(d) => saveSeccion("notas", d)}
            renderEdit={{
              initial: () => ({ notas_desarrollo: m.notas_desarrollo || "" }),
              form: (d, set) => <Textarea rows={5} value={d.notas_desarrollo} onChange={(e) => set({ ...d, notas_desarrollo: e.target.value })} placeholder="Notas internas (no es descripción de venta)" data-testid="notas-textarea" />,
            }}
          >
            <p className="text-sm text-slate-600 whitespace-pre-wrap">{m.notas_desarrollo || "Sin notas."}</p>
          </SectionCard></div>
        </div>

        {/* ---------------- Barra lateral ---------------- */}
        <div className="contents lg:block lg:col-span-4 lg:space-y-6">
          {/* Estatus */}
          <div className="order-1 md:col-span-1"><Card className="border-slate-200 shadow-sm p-5" data-testid="section-estatus">
            <h3 className="font-head font-bold tracking-tight">Estatus del proceso</h3>
            <div className="mt-3 flex items-end justify-between">
              <span className="font-head text-4xl font-extrabold tracking-tight tabular-nums">{est.porcentaje}%</span>
              <span className="text-sm text-slate-400">{est.completados} / {est.total}</span>
            </div>
            <Progress value={est.porcentaje} className="h-2 mt-2" />
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Último completado</span><span className="font-medium text-right">{est.ultimo_paso ? etiquetaPaso(est.ultimo_paso, ambiguos) : "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Siguiente pendiente</span><span className="font-medium text-right text-blue-600">{est.completo ? "✓ Completado" : etiquetaPaso(est.siguiente_paso, ambiguos) || "—"}</span></div>
            </div>
          </Card></div>

          {/* Tiempos y meta */}
          <div className="order-5 md:col-span-1"><SectionCard
            title="Tiempos y meta" icon={Clock} section="planeacion" canEdit={puedeSeccion("planeacion") && m.ruta_info?.con_prototipo !== false} testid="section-tiempos"
            onSave={(d) => {
              const changed = (d.fecha_meta_prototipo || "") !== (m.fecha_meta_prototipo || "");
              if (changed && !(d.motivo || "").trim()) {
                throw { response: { data: { detail: "Debes capturar un motivo para cambiar la fecha meta de prototipo" } } };
              }
              return saveSeccion("planeacion", d);
            }}
            renderEdit={{
              initial: () => ({ fecha_meta_prototipo: m.fecha_meta_prototipo || "", motivo: "" }),
              form: (d, set) => {
                const esDesarrollo = !user?.es_admin && user?.role === "desarrollo";
                const usados = m.cambios_fecha_meta_prototipo_desarrollo || 0;
                const restantes = Math.max(0, 2 - usados);
                const bloqueadoDesarrollo = esDesarrollo && usados >= 2;
                const changed = (d.fecha_meta_prototipo || "") !== (m.fecha_meta_prototipo || "");
                return (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Fecha meta del prototipo</Label>
                    <FechaInput value={d.fecha_meta_prototipo} disabled={bloqueadoDesarrollo} onChange={(e) => set({ ...d, fecha_meta_prototipo: e.target.value })} data-testid="meta-prototipo-input" />
                    <p className="text-[11px] text-slate-400">Fecha objetivo para tener el prototipo listo (fin de la fase Prototipo).</p>
                  </div>
                  {esDesarrollo && (
                    bloqueadoDesarrollo ? (
                      <p className="text-[12px] text-red-600 bg-red-50 rounded-md px-2.5 py-1.5" data-testid="meta-limite-msg">Ya se alcanzó el límite de 2 cambios de Desarrollo para este producto. Contacta a un administrador.</p>
                    ) : (
                      <p className="text-[11px] text-amber-600">Cambios de Desarrollo restantes para este producto: {restantes} de 2 (contador compartido).</p>
                    )
                  )}
                  {changed && !bloqueadoDesarrollo && (
                    <div className="space-y-1.5">
                      <Label>Motivo del cambio <span className="text-red-500">*</span></Label>
                      <Textarea rows={2} value={d.motivo} onChange={(e) => set({ ...d, motivo: e.target.value })} placeholder="Explica por qué cambias la fecha meta…" data-testid="meta-motivo-input" />
                    </div>
                  )}
                </div>
                );
              },
            }}
          >
            {(() => {
              const t = m.tiempos;
              const mp = t.meta_prototipo;
              const metaTint = {
                cumplida: "bg-emerald-50 text-emerald-700",
                tarde: "bg-amber-50 text-amber-700",
                vencida: "bg-red-50 text-red-700",
                en_tiempo: "bg-blue-50 text-blue-700",
              };
              const metaLabel = {
                cumplida: "Cumplida en meta",
                tarde: "Prototipo listo (fuera de meta)",
                vencida: "Meta vencida",
                en_tiempo: `Faltan ${mp?.dias_restantes} días`,
              };
              return (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wide">Fecha solicitud</div>
                      <div className="text-sm text-slate-800 mt-0.5" data-testid="tiempo-fecha-creacion">{t.fecha_creacion ? <>{fechaSolo(t.fecha_creacion)}<PeriodoTag date={t.fecha_creacion} /></> : "—"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wide">Tiempo real desde la solicitud</div>
                      <div className="text-sm font-semibold text-slate-800 mt-0.5" data-testid="tiempo-real-total">{t.tiempo_real_dias == null ? "—" : dias(t.tiempo_real_dias)}{!t.completo && t.tiempo_real_dias != null ? " (en curso)" : ""}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wide">Fecha primer avance</div>
                      <div className="text-sm text-slate-800 mt-0.5" data-testid="tiempo-primer-avance">{t.fecha_primer_avance ? <>{fechaSolo(t.fecha_primer_avance)}<PeriodoTag date={t.fecha_primer_avance} /></> : "Sin avances aún"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wide">Días de reacción</div>
                      <div className="text-sm text-slate-800 mt-0.5" data-testid="tiempo-dias-reaccion">{t.fecha_primer_avance && t.dias_reaccion != null ? dias(t.dias_reaccion) : "—"}</div>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Fecha meta prototipo</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {m.ruta_info?.con_prototipo === false
                        ? <span className="text-sm text-slate-400">No aplica (ruta sin prototipo)</span>
                        : <span className="text-sm text-slate-800">{fechaSolo(t.fecha_meta_prototipo)}<PeriodoTag date={t.fecha_meta_prototipo} /></span>}
                      {mp && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${metaTint[mp.estado]}`} data-testid="meta-estado">{metaLabel[mp.estado]}</span>}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-slate-400 uppercase tracking-wide">Tiempo neto por fase</span>
                      <span className="text-[10px] text-slate-400 normal-case">reinicia con cada Reajuste</span>
                    </div>
                    <div className="space-y-2">
                      {t.etapas.map((e) => (
                        <div key={e.fase} className="flex items-center gap-2" data-testid={`etapa-tiempo-${e.fase}`}>
                          <span className="h-2 w-2 rounded-full shrink-0" style={{ background: FASE_DOT[e.fase] }} />
                          <span className="text-sm text-slate-600 flex-1 min-w-0 truncate">{e.fase}</span>
                          {e.estado === "completa" && <span className="text-xs font-semibold text-slate-700 tabular-nums" data-testid={`etapa-neto-${e.fase}`}>{dias(e.dias_neto)}</span>}
                          {e.estado === "en_progreso" && <span className="text-xs font-medium text-blue-600 tabular-nums" data-testid={`etapa-neto-${e.fase}`}>{dias(e.dias_neto)} · en curso</span>}
                          {e.estado === "pendiente" && <span className="text-xs text-slate-300">pendiente</span>}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-700">Total neto en Desarrollo</span>
                      <span className="text-sm font-bold text-slate-800 tabular-nums" data-testid="neto-desarrollo-total">{dias(t.neto_desarrollo_dias || 0)}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </SectionCard></div>

          {/* Organización */}
          <div className="order-6 md:col-span-1"><SectionCard
            title="Organización del producto" section="organizacion" canEdit={puedeOrg || puedeDisenador} testid="section-organizacion"
            onSave={(d) => saveSeccion("organizacion", d)}
            renderEdit={{
              initial: () => ({ ...m.organizacion }),
              form: (d, set) => (
                <div className="space-y-3">
                  {[["categoria", "Categoría"], ["genero", "Género"], ["estrategia", "Estrategia"], ["ciclo_vida", "Ciclo de vida"]].map(([k, label]) => (
                    <div key={k} className="space-y-1.5">
                      <Label>{label}</Label>
                      <Select value={d[k]} disabled={!puedeOrg} onValueChange={(v) => set({ ...d, [k]: v })}>
                        <SelectTrigger data-testid={`org-${k}`}><SelectValue placeholder="Selecciona" /></SelectTrigger>
                        <SelectContent>{(listas[k]?.valores || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ))}
                  <div className="space-y-1.5"><Label>Línea</Label><Input value={d.linea} disabled={!puedeOrg} onChange={(e) => set({ ...d, linea: e.target.value })} data-testid="org-linea" /></div>
                  <div className="space-y-1.5"><Label>Proveedor / maquilador</Label><Input value={d.proveedor} disabled={!puedeOrg} onChange={(e) => set({ ...d, proveedor: e.target.value })} data-testid="org-proveedor" /></div>
                  <div className="space-y-1.5">
                    <Label>Diseñador asignado</Label>
                    <Select value={d.disenador_id || "none"} disabled={!puedeDisenador} onValueChange={(v) => set({ ...d, disenador_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="org-disenador"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {disenadores.map((u) => <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ),
            }}
          >
            <div className="space-y-3">
              <Field label="Categoría" value={m.organizacion.categoria} />
              <Field label="Género" value={m.organizacion.genero} />
              <Field label="Estrategia" value={m.organizacion.estrategia} />
              <Field label="Ciclo de vida" value={m.organizacion.ciclo_vida} />
              <Field label="Línea" value={m.organizacion.linea} />
              <Field label="Proveedor / maquilador" value={m.organizacion.proveedor} />
              <Field label="Diseñador asignado" value={disenadores.find((u) => u.id === m.organizacion.disenador_id)?.nombre || "Sin asignar"} />
            </div>
          </SectionCard></div>

          {/* Documentos */}
          <div className="order-9 md:col-span-2"><Card className="border-slate-200 shadow-sm" data-testid="section-documentos">
            <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
              <Paperclip size={16} className="text-slate-400" />
              <h3 className="font-head font-bold tracking-tight">Documentos del modelo</h3>
            </div>
            <div className="p-3">
              {archivos.filter((a) => a.tipo === "documento_paso").length === 0 ? (
                <p className="text-sm text-slate-400 px-2 py-3">Sin documentos subidos.</p>
              ) : (
                <div className="space-y-1">
                  {archivos.filter((a) => a.tipo === "documento_paso").map((a) => {
                    const inner = (
                      <>
                        <FileText size={16} className="text-slate-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-slate-700 truncate">{a.original_filename}</div>
                          <div className="text-[11px] text-slate-400">Paso {a.paso_numero} · v{a.version} · {fechaHora(a.uploaded_at)}</div>
                        </div>
                        {a.is_current && <span className="text-[10px] font-semibold text-emerald-600 shrink-0">Actual</span>}
                      </>
                    );
                    return puedeDescargar ? (
                      <a key={a.id} href={fileUrl(a.id)} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`doc-item-${a.id}`}>
                        {inner}
                      </a>
                    ) : (
                      <div key={a.id} className="flex items-center gap-2.5 px-2 py-2 rounded-lg cursor-default opacity-90" data-testid={`doc-item-${a.id}`} title="Tu rol no puede descargar documentos">
                        {inner}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card></div>

          {/* Historial */}
          <div className="order-10 md:col-span-2"><Card className="border-slate-200 shadow-sm" data-testid="section-historial">
            <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
              <History size={16} className="text-slate-400" />
              <h3 className="font-head font-bold tracking-tight">Historial de cambios</h3>
            </div>
            <div className="p-3 max-h-96 overflow-y-auto thin-scroll">
              {historial.length === 0 ? (
                <p className="text-sm text-slate-400 px-2 py-3">Sin actividad.</p>
              ) : (
                <div className="space-y-3">
                  {historial.map((h) => (
                    <div key={h.id} className="flex gap-3">
                      <div className="mt-1 h-2 w-2 rounded-full bg-slate-300 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-slate-700">{h.descripcion}</div>
                        <div className="text-[11px] text-slate-400">
                          {h.usuario_nombre} <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${colorRol(h.usuario_rol)}`}>{labelRol(h.usuario_rol)}</span> · {fechaHora(h.fecha)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </Card></div>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm p-5 mt-6" data-testid="modelo-comentarios">
        <h3 className="font-head font-bold tracking-tight flex items-center gap-2 mb-3"><MessageSquare size={16} /> Comentarios</h3>
        <div className="space-y-3 mb-4">
          {(m.comentarios || []).length === 0 && <p className="text-sm text-slate-400">Aún no hay comentarios.</p>}
          {(m.comentarios || []).map((c) => (
            <CommentItem key={c.id} c={c} testidPrefix="modelo-comentario" />
          ))}
        </div>
        {puedeAccion("comentar") && <CommentBox users={mencionables} onSubmit={comentar} testidPrefix="modelo-comentario" />}
      </Card>

      <ModeloDialogs
        m={m} colecciones={colecciones}
        replaceDlg={replaceDlg} setReplaceDlg={setReplaceDlg} confirmReplace={confirmReplace}
        dupOpen={dupOpen} setDupOpen={setDupOpen} dupNombre={dupNombre} setDupNombre={setDupNombre}
        dupColeccion={dupColeccion} setDupColeccion={setDupColeccion} dupSel={dupSel} setDupSel={setDupSel}
        confirmarDuplicar={confirmarDuplicar} accionBusy={accionBusy}
        reasignOpen={reasignOpen} setReasignOpen={setReasignOpen} reasignColeccion={reasignColeccion}
        setReasignColeccion={setReasignColeccion} confirmarReasignar={confirmarReasignar}
        reajusteOpen={reajusteOpen} setReajusteOpen={setReajusteOpen}
        reajustePaso={reajustePaso} setReajustePaso={setReajustePaso}
        reajusteMotivo={reajusteMotivo} setReajusteMotivo={setReajusteMotivo} confirmarReajuste={confirmarReajuste}
        depuradoOpen={depuradoOpen} setDepuradoOpen={setDepuradoOpen}
        depuradoMotivo={depuradoMotivo} setDepuradoMotivo={setDepuradoMotivo} confirmarDepurado={confirmarDepurado}
        reactivarOpen={reactivarOpen} setReactivarOpen={setReactivarOpen}
        reactivarMotivo={reactivarMotivo} setReactivarMotivo={setReactivarMotivo} confirmarReactivar={confirmarReactivar}
        lightbox={lightbox} setLightbox={setLightbox}
      />
    </div>
  );
}